import { useState, useEffect } from 'react';
import { Container, Card, Button, Alert } from 'react-bootstrap';
import { useCampaign } from '../../../apps/hooks/campaign/useCampaign';
import type { Campaign, CampaignStatus } from '../../../infrastructure/CampaignClient';
import { CampaignFilterStatus } from '../../../infrastructure/CampaignClient';
import type { FormData } from './components';
import {
  CampaignList,
  CampaignFilters,
  CampaignDetailModal,
  CreateCampaignModal,
  UpdateCampaignModal,
  DeleteCampaignModal,
} from './components';

export default function CampaignPage() {
  const {
    listCampaignsWithFilter,
    createCampaignMutation,
    updateCampaignMutation,
    updateCampaignStatusMutation,
    deleteCampaignMutation,
  } = useCampaign();

  // Filter state
  const [selectedStatus, setSelectedStatus] = useState<CampaignFilterStatus>(CampaignFilterStatus.ALL);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Selected campaign state
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);

  // Error and success states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch campaigns with current filter
  const campaignsQuery = listCampaignsWithFilter({
    status: selectedStatus,
    limit: 100,
  });

  const { data: campaignData, isLoading, refetch } = campaignsQuery;
  const campaigns = campaignData?.campaigns || [];

  // Clear messages after some time
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return;
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
    return;
  }, [error]);

  // Event handlers
  const handleCreateCampaign = async (formData: FormData) => {
    try {
      setError(null);
      await createCampaignMutation.mutateAsync({
        name: formData.name,
        startAt: formData.startAt,
        endAt: formData.endAt,
        status: formData.status,
      });
      setShowCreateModal(false);
      setSuccess('Campaign created successfully!');
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    }
  };

  const handleUpdateCampaign = async (formData: FormData) => {
    if (!selectedCampaign) {
      return;
    }

    try {
      setError(null);
      await updateCampaignMutation.mutateAsync({
        id: selectedCampaign.id,
        request: {
          name: formData.name,
          startAt: formData.startAt,
          endAt: formData.endAt,
        },
      });

      setShowUpdateModal(false);
      setSelectedCampaign(null);
      setSuccess('Campaign updated successfully!');
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update campaign');
    }
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) {
      return;
    }

    try {
      setError(null);
      await deleteCampaignMutation.mutateAsync(selectedCampaign.id);
      setShowDeleteModal(false);
      setSelectedCampaign(null);
      setSuccess('Campaign deleted successfully!');
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete campaign');
    }
  };

  const handleStatusChange = async (campaign: Campaign, newStatus: CampaignStatus) => {
    try {
      setError(null);
      await updateCampaignStatusMutation.mutateAsync({
        id: campaign.id,
        status: newStatus,
      });
      setSuccess(`Campaign status updated to ${newStatus}!`);
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update campaign status');
    }
  };

  const handleView = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailModal(true);
  };

  const handleEdit = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowUpdateModal(true);
  };

  const handleDelete = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowDeleteModal(true);
  };

  const handleEditFromDetail = () => {
    setShowDetailModal(false);
    setShowUpdateModal(true);
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Campaign Management</h2>
        <Button variant="primary" onClick={() => setShowCreateModal(true)} disabled={createCampaignMutation.isPending}>
          Create New Campaign
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <CampaignFilters
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        totalCampaigns={campaigns.length}
      />

      <Card>
        <Card.Body>
          <CampaignList
            campaigns={campaigns}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            loading={isLoading}
          />
        </Card.Body>
      </Card>

      {/* Create Campaign Modal */}
      <CreateCampaignModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSubmit={handleCreateCampaign}
        isSubmitting={createCampaignMutation.isPending}
      />

      {/* Campaign Detail Modal */}
      <CampaignDetailModal
        show={showDetailModal}
        campaign={selectedCampaign}
        onHide={() => {
          setShowDetailModal(false);
          setSelectedCampaign(null);
        }}
        onEdit={handleEditFromDetail}
      />

      {/* Update Campaign Modal */}
      <UpdateCampaignModal
        show={showUpdateModal}
        campaign={selectedCampaign}
        onHide={() => {
          setShowUpdateModal(false);
          setSelectedCampaign(null);
        }}
        onSubmit={handleUpdateCampaign}
        isSubmitting={updateCampaignMutation.isPending}
      />

      {/* Delete Campaign Modal */}
      <DeleteCampaignModal
        show={showDeleteModal}
        campaign={selectedCampaign}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedCampaign(null);
        }}
        onConfirm={handleDeleteCampaign}
        isDeleting={deleteCampaignMutation.isPending}
      />
    </Container>
  );
}
